// src/admin/AdminTermsPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getCurrentTerms, 
  updateCurrentTerms, 
  getTermsHistory,
  validateTermsContent,
  getTermsStats,
  getTermsTemplate
} from '../services/termsService';

const SUPER_ADMIN_EMAILS = [
  'matthew@backpass.co',
  'mgriff7@gmail.com',
  'mhgbackpass@gmail.com',
  'mhgcommercial@gmail.com'
];

// Note: React-Quill needs to be installed with: npm install react-quill quill
// For now, we'll use a textarea but will upgrade to React-Quill
// import ReactQuill from 'react-quill';
// import 'react-quill/dist/quill.snow.css';

export default function AdminTermsPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Terms data
  const [currentTerms, setCurrentTerms] = useState(null);
  const [termsHistory, setTermsHistory] = useState([]);
  const [content, setContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({ characterCount: 0, wordCount: 0, lineCount: 0 });
  
  // Check if current user is super admin
  const isSuperAdmin = currentUser && SUPER_ADMIN_EMAILS.includes(currentUser.email?.toLowerCase());

  // Load current terms and history on mount
  useEffect(() => {
    loadTermsData();
  }, []);

  // Update stats when content changes
  useEffect(() => {
    const newStats = getTermsStats(content);
    setStats(newStats);
  }, [content]);

  const loadTermsData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load current terms
      const terms = await getCurrentTerms();
      setCurrentTerms(terms);
      setContent(terms?.content || '');
      
      // Load history
      const history = await getTermsHistory();
      setTermsHistory(history);
      
    } catch (err) {
      console.error('Error loading terms data:', err);
      setError(err.message || 'Failed to load terms data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate content
      const validation = validateTermsContent(content);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      // Update terms
      const updatedTerms = await updateCurrentTerms(content, currentUser.email);
      setCurrentTerms(updatedTerms);
      setSuccess('Terms and conditions updated successfully!');
      
      // Reload history to show the new version
      const updatedHistory = await getTermsHistory();
      setTermsHistory(updatedHistory);
      
    } catch (err) {
      console.error('Error saving terms:', err);
      setError(err.message || 'Failed to save terms');
    } finally {
      setSaving(false);
    }
  };

  const resetContent = () => {
    if (window.confirm('Are you sure you want to reset to the current saved version? All unsaved changes will be lost.')) {
      setContent(currentTerms?.content || '');
      setError('');
      setSuccess('');
    }
  };

  const loadTemplate = () => {
    if (window.confirm('This will replace your current content with the template. Continue?')) {
      setContent(getTermsTemplate());
      setError('');
      setSuccess('Template loaded. Remember to save your changes.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp.toDate();
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <h2>Loading Terms & Conditions...</h2>
      </div>
    );
  }

  // Show access denied for non-super admins
  if (!isSuperAdmin) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '32px',
          backgroundColor: '#f8f9fa',
          border: '2px solid #dc3545',
          borderRadius: '8px'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>Access Denied</h2>
          <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.5' }}>
            Only Super Administrators can edit Terms & Conditions. 
            This restriction ensures legal compliance and content consistency.
          </p>
          <p style={{ color: '#999', fontSize: '14px', marginTop: '16px' }}>
            Current user: {currentUser?.email}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Important Notice */}
      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}>
        <span style={{ fontSize: '24px' }}>⚠️</span>
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#856404' }}>Important Notice</h4>
          <p style={{ margin: '0', color: '#856404', fontSize: '14px' }}>
            The public Terms & Conditions are currently served from a <strong>hardcoded HTML file</strong> located at{' '}
            <code style={{ backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px' }}>
              frontend/public/terms.html
            </code>
            . Changes made here will <strong>NOT</strong> be reflected on the public website. 
            To update the public terms, you must manually edit the HTML file.
          </p>
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px',
        borderBottom: '2px solid #eee',
        paddingBottom: '16px'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', color: '#333' }}>Terms & Conditions Management</h2>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            {currentTerms ? `Current Version: ${currentTerms.version}` : 'No terms published yet'} | 
            Last updated: {currentTerms ? formatDate(currentTerms.lastUpdated) : 'Never'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowHistory(!showHistory)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {showHistory ? 'Hide' : 'View'} History ({termsHistory.length})
          </button>
          <button
            onClick={() => setShowPreview(!showPreview)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {showPreview ? 'Hide' : 'Show'} Preview
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px 16px',
          marginBottom: '16px',
          border: '1px solid #f5c6cb',
          borderRadius: '4px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '12px 16px',
          marginBottom: '16px',
          border: '1px solid #c3e6cb',
          borderRadius: '4px'
        }}>
          <strong>Success:</strong> {success}
        </div>
      )}

      {/* Stats Bar */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        fontSize: '14px',
        color: '#666'
      }}>
        <span><strong>Characters:</strong> {stats.characterCount.toLocaleString()}</span>
        <span><strong>Words:</strong> {stats.wordCount.toLocaleString()}</span>
        <span><strong>Lines:</strong> {stats.lineCount}</span>
      </div>

      {/* Main Content Area */}
      <div style={{ display: 'flex', gap: '24px' }}>
        
        {/* Editor Section */}
        <div style={{ flex: showPreview ? '1' : '2' }}>
          <h3 style={{ marginBottom: '12px', color: '#333' }}>Edit Terms & Conditions</h3>
          
          {/* Editor - Note: This will be replaced with React-Quill */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter your terms and conditions here..."
            style={{
              width: '100%',
              height: '500px',
              padding: '16px',
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: '1.5',
              resize: 'vertical',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#007bff'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
          />
          
          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginTop: '16px',
            justifyContent: 'space-between'
          }}>
            <button
              onClick={loadTemplate}
              disabled={saving}
              style={{
                padding: '10px 20px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              Load Template
            </button>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={resetContent}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1
                }}
              >
                Reset to Saved
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !content.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: saving ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (saving || !content.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (saving || !content.trim()) ? 0.6 : 1
                }}
              >
                {saving ? 'Publishing...' : 'Publish Terms'}
              </button>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        {showPreview && (
          <div style={{ flex: '1', borderLeft: '2px solid #eee', paddingLeft: '24px' }}>
            <h3 style={{ marginBottom: '12px', color: '#333' }}>Preview</h3>
            <div style={{
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              height: '500px',
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: 'white',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              {content ? (
                <div dangerouslySetInnerHTML={{ __html: content }} />
              ) : (
                <p style={{ color: '#999', fontStyle: 'italic' }}>
                  No content to preview. Enter some terms and conditions in the editor.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Version History */}
      {showHistory && (
        <div style={{
          marginTop: '32px',
          padding: '24px',
          border: '2px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa'
        }}>
          <h3 style={{ marginBottom: '16px', color: '#333' }}>Version History</h3>
          {termsHistory.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>No previous versions found.</p>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {termsHistory.map((version, index) => (
                <div key={index} style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <strong style={{ color: '#495057' }}>Version {version.version}</strong>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {formatDate(version.lastUpdated)} by {version.updatedBy}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6c757d',
                    maxHeight: '60px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {version.content?.substring(0, 200)}...
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}